export const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';
export const DEFAULT_POSTHOG_UI_HOST = 'https://eu.posthog.com';
export const POSTHOG_SDK_DEFAULTS = '2026-01-30' as const;
export const DEFAULT_ANALYTICS_ENVIRONMENT = 'unknown';

export type AnalyticsErrorCategory =
  | 'auth'
  | 'conflict'
  | 'network'
  | 'not_found'
  | 'permission'
  | 'provider'
  | 'rate_limited'
  | 'timeout'
  | 'validation'
  | 'unknown';

export type AnalyticsAutomationMode = 'disabled' | 'schedule' | 'webhook' | 'schedule_and_webhook';

export type ActivationMilestone =
  | 'account_created'
  | 'email_verified'
  | 'plan_unlocked'
  | 'framer_connected'
  | 'source_preview_succeeded'
  | 'config_saved'
  | 'first_sync_requested'
  | 'first_sync_succeeded'
  | 'capped_import'
  | 'automation_enabled'
  | 'cancellation';

export type ActivationEventContext = {
  activationMilestone?: ActivationMilestone | null;
  activationMilestones?: ActivationMilestone[];
  billingCycle?: string | null;
  collectionName?: string | null;
  configId?: string | null;
  importedCount?: number | null;
  plan?: string | null;
  projectId?: string | null;
  provider?: string | null;
  recordImportLimit?: number | null;
  runId?: string | null;
  skippedCount?: number | null;
  sourceKind?: string | null;
};

export function normalizeAnalyticsString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function classifyAnalyticsError(error: unknown): AnalyticsErrorCategory {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const normalized = message.trim().toLowerCase();

  if (!normalized) {
    return 'unknown';
  }

  if (
    normalized.includes('unauthorized') ||
    normalized.includes('invalid api key') ||
    normalized.includes('invalid framer credentials') ||
    normalized.includes('invalid webhook signature') ||
    normalized.includes('missing webhook signature')
  ) {
    return 'auth';
  }

  if (
    normalized.includes('forbidden') ||
    normalized.includes('does not have access') ||
    normalized.includes('not enough rights') ||
    normalized.includes('nicht genügend rechte') ||
    normalized.includes('upgrade your subscription') ||
    normalized.includes('limit reached')
  ) {
    return 'permission';
  }

  if (
    normalized.includes('validation failed') ||
    normalized.includes('required') ||
    normalized.includes('invalid url') ||
    normalized.includes('expected json')
  ) {
    return 'validation';
  }

  if (normalized.includes('already exists') || normalized.includes('already connected')) {
    return 'conflict';
  }

  if (normalized.includes('not found')) {
    return 'not_found';
  }

  if (normalized.includes('rate limit') || normalized.includes('429')) {
    return 'rate_limited';
  }

  if (normalized.includes('timed out') || normalized.includes('timeout')) {
    return 'timeout';
  }

  if (
    normalized.includes('failed to fetch') ||
    normalized.includes('network') ||
    normalized.includes('econnreset') ||
    normalized.includes('enotfound')
  ) {
    return 'network';
  }

  if (
    normalized.includes('framer') ||
    normalized.includes('polar') ||
    normalized.includes('provider') ||
    normalized.includes('webhook')
  ) {
    return 'provider';
  }

  return 'unknown';
}

export function resolveAnalyticsAutomationMode(args: {
  scheduleEnabled: boolean;
  webhookEnabled: boolean;
}): AnalyticsAutomationMode {
  const { scheduleEnabled, webhookEnabled } = args;

  if (scheduleEnabled && webhookEnabled) {
    return 'schedule_and_webhook';
  }
  if (scheduleEnabled) {
    return 'schedule';
  }
  if (webhookEnabled) {
    return 'webhook';
  }
  return 'disabled';
}

export function stripUndefinedValues(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined));
}

function normalizeActivationMilestones(context: ActivationEventContext): ActivationMilestone[] {
  return Array.from(
    new Set(
      [context.activationMilestone ?? undefined, ...(context.activationMilestones ?? [])].filter(
        (entry): entry is ActivationMilestone => Boolean(entry),
      ),
    ),
  );
}

export function buildActivationEventProperties(
  context: ActivationEventContext,
  properties: Record<string, unknown> = {},
): Record<string, unknown> {
  const milestones = normalizeActivationMilestones(context);
  const standardizedProperties = stripUndefinedValues({
    activation_milestone: milestones[0],
    activation_milestones: milestones.length > 0 ? milestones : undefined,
    billing_cycle: context.billingCycle,
    collection_name: context.collectionName,
    config_id: context.configId,
    imported_count: context.importedCount,
    plan: context.plan,
    project_id: context.projectId,
    provider: context.provider,
    record_import_limit: context.recordImportLimit,
    run_id: context.runId,
    skipped_count: context.skippedCount,
    source_kind: context.sourceKind,
  });

  return stripUndefinedValues({
    ...properties,
    ...standardizedProperties,
  });
}
